import React, { useState, useMemo } from 'react';
import { Platform, ScrollView, TouchableOpacity, View } from 'react-native'

import api from "../../../../utils/api"
import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import IcomoonIcon from '../../../../components/IcomoonIcon';
import { Illustration } from '../../../../components/Illustration';
import { useQuery } from '@tanstack/react-query';
import { useNavigation } from '@react-navigation/native';
import Header from '../../../../navigation/Header';
import IconButton from '../../../../components/IconButton';
import useActiveAdminCommunity from '@/hooks/useActiveAdminCommunity';
import useOpenDetailParam from '@/hooks/useOpenDetailParam';
import NewAnnouncementModal from './NewAnnouncementModal';
import dayjs from 'dayjs';


const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"]


function AnnouncementRow({ announcement, selected }) {
  const navigation = useNavigation()
  const dateStr = dayjs(announcement.created_at).format('D MMM')

  function goToDetail() {
    navigation.navigate('Admin.Announcement.Detail', { announcementId: announcement.uuid })
  }

  return (
    <TouchableOpacity
      style={tw.style(`row items-center px-3 py-3 rounded-xl border border-transparent`, selected && `bg-selected border-selected-border`)}
      onPress={goToDetail}
    >
      <View style={tw`flex-1`}>
        <Text style={tw`text-secondary font-500 text-base`} numberOfLines={1}>{announcement.title}</Text>

        {
          announcement.text &&
            <Text style={tw`text-gray-400`} numberOfLines={1}>{announcement.text}</Text>
        }
      </View>

      <Text style={tw`text-gray-400 text-sm ml-2`}>{dateStr}</Text>
      <IcomoonIcon name='chevron-right' color={tw.color('secondary')} size={13} style={tw`ml-2`} />
    </TouchableOpacity>
  )
}


export default function AdminAnnouncementsScreen({ navigation, route }) {
  const { communityId } = useActiveAdminCommunity()
  const openAnnouncementId = useOpenDetailParam(navigation, 'Admin.Announcement.Detail', 'announcementId')
  const [showNewModal, setShowNewModal] = useState(false);

  const { isLoading, error, data, refetch } = useQuery({
    queryKey: ['community.announcements', communityId],
    queryFn: () => api.get(`/communities/${communityId}/announcements`).then(response => response.data),
  })

  const announcementsList = useMemo(() => {
    return Array.isArray(data) ? data : []
  }, [data])

  // Agrupar por mes (más reciente primero).
  const monthGroups = useMemo(() => {
    const groups = {}
    announcementsList.forEach(a => {
      const d = dayjs(a.created_at)
      const key = d.format('YYYY-MM')
      if (!groups[key]) {
        groups[key] = { key, label: `${MONTHS[d.month()]} ${d.year()}`, items: [] }
      }
      groups[key].items.push(a)
    })
    return Object.values(groups)
      .sort((a, b) => (a.key < b.key ? 1 : -1))
      .map(g => ({ ...g, items: g.items.sort((x, y) => (x.created_at < y.created_at ? 1 : -1)) }))
  }, [announcementsList])


  return (
    <View style={tw`flex-1 bg-[#F3F2F3]`}>
      <Header style={tw`bg-secondary rounded-b-0 mb-0`} showBackButton={Platform.OS !== 'web'} backButtonColor='white'>
        <View style={tw``}>
          <Text style={tw`text-white text-lg font-600`}>Comunicados</Text>
          {
            isLoading ?
              <Text style={tw`text-white/50`}>Cargando...</Text>
            :
              <Text style={tw`text-white/80`}>{announcementsList.length} publicados</Text>
          }
        </View>
      </Header>

      <ScrollView style={tw`flex-1`} contentContainerStyle={tw`py-4 px-2 pb-20`}>
        {
          !isLoading && monthGroups.length === 0 &&
            <Text style={tw`text-gray-400 text-center mt-20`}>Sin comunicados</Text>
        }

        {
          monthGroups.map(group =>
            <View key={group.key} style={tw`mb-6`}>
              <View style={tw`bg-gray-200 mb-4 self-center px-6 py-0.5 rounded-full border border-gray-300`}>
                <Text style={tw`text-gray-400`}>{group.label}</Text>
              </View>

              <View style={tw`bg-white border border-gray-200 rounded-2xl p-1`}>
                {
                  group.items.map((announcement, i) => {
                    const selected = announcement.uuid === openAnnouncementId
                    // Separador entre filas; se oculta junto a la seleccionada para no cortar su borde.
                    const prevSelected = i > 0 && group.items[i - 1].uuid === openAnnouncementId
                    return (
                      <React.Fragment key={announcement.uuid}>
                        {i > 0 && <View style={tw.style(`h-px mx-3 bg-gray-200/70`, (selected || prevSelected) && `bg-transparent`)} />}
                        <AnnouncementRow announcement={announcement} selected={selected} />
                      </React.Fragment>
                    )
                  })
                }
              </View>
            </View>
          )
        }
      </ScrollView>

      <IconButton
        title={"Nuevo comunicado"}
        color='secondary'
        textColor='white'
        style={tw`absolute bottom-4 self-center`}
        onPress={() => setShowNewModal(true)}
      />

      {
        showNewModal &&
          <NewAnnouncementModal
            communityId={communityId}
            visible
            onRequestClose={() => setShowNewModal(false)}
            onAnnouncementCreated={() => {
              setShowNewModal(false)
              refetch()
            }}
          />
      }
    </View>
  );
}
